/**
 * GFG-364 — the shell every contacts dialog shares: the three paper sizes, the bordered close
 * button, the ochre text link, the table cell classes and the title/subtitle/close header. Each
 * dialog used to carry its own copy of these; the values here are what they rendered.
 */
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import type { ReactNode } from "react";
import { COPY } from "@/constants/campaignContacts";

const paperSx = (width: string) => ({
  width,
  maxWidth: width,
  m: 2,
  p: "26px 28px",
  borderRadius: "var(--radius-card)",
});

/** Prototype: `.bc-modal.cmp-contacts-modal { max-width: min(96vw, 1280px); padding: 26px 28px; }` */
export const CONTACTS_TABLE_PAPER_SX = paperSx("min(96vw, 1280px)");

/** The budget history list: a table, but narrower than the contacts one. */
export const CONTACTS_HISTORY_PAPER_SX = paperSx("min(96vw, 960px)");

/** The single-purpose form dialogs: add a recipient, remove a contact. */
export const CONTACTS_FORM_PAPER_SX = paperSx("min(92vw, 520px)");

export const CONTACTS_CLOSE_BUTTON_SX = {
  width: 36,
  height: 36,
  flexShrink: 0,
  border: "1px solid var(--color-smoke)",
  borderRadius: "var(--radius-btn)",
  color: "var(--color-graphite)",
  "&:hover": { borderColor: "var(--color-dark-dust)", color: "var(--color-rodeo-black)" },
} as const;

/** Prototype `.cmp-bounce-close-link`: the ochre uppercase text link (footer Close, history). */
export const CONTACTS_TEXT_LINK_SX = {
  p: 0,
  minWidth: 0,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--color-good-ochre)",
  "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
} as const;

export const CONTACTS_TH_CLASS =
  "sticky top-0 z-[1] border-b border-smoke bg-white px-2.5 pt-3.5 pb-2.5 text-left " +
  "text-[11px] font-bold uppercase tracking-[1px] text-dust-ink";

export const CONTACTS_TD_CLASS = "border-b border-smoke px-2.5 py-3 text-[14px]";

interface ContactsDialogHeaderProps {
  /** The id the `Dialog`'s `aria-labelledby` points at. */
  titleId: string;
  title: string;
  subtitle: ReactNode;
  onClose: () => void;
  /** The wizard's look: a `min-w-0` text column and a 13px Lato subtitle. */
  compact?: boolean;
}

export function ContactsDialogHeader({
  titleId,
  title,
  subtitle,
  onClose,
  compact = false,
}: ContactsDialogHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className={compact ? "min-w-0" : undefined}>
        <h2 id={titleId} className="text-[20px] font-bold text-rodeo-black">
          {title}
        </h2>
        <p
          className={
            compact ? "mt-1 font-lato text-[13px] text-graphite" : "mt-1 text-[14px] text-graphite"
          }
        >
          {subtitle}
        </p>
      </div>
      <IconButton aria-label={COPY.closeLabel} onClick={onClose} sx={CONTACTS_CLOSE_BUTTON_SX}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
}
